import { useState, useEffect, useCallback } from "react";
import { searchService, dashboardService } from "../../services/api";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "../../contexts/TranslationContext";

const useTransferNews = () => {
  const navigate = useNavigate();
  const { currentLanguage, changeLanguage } = useTranslation();
  // Normalise once: APITube wants a 2-letter lowercase code, no region tag.
  const apiLanguage = (currentLanguage || "en").toLowerCase().split("-")[0];

  // Top News state
  const [topNews, setTopNews] = useState([]);
  const [topNewsError, setTopNewsError] = useState(null);
  const [isLoadingTopNews, setIsLoadingTopNews] = useState(true);

  // Initial load state
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Shared filter options
  const [positions, setPositions] = useState([]);
  const [countries, setCountries] = useState([]);

  // Load filter options
  const loadFilterOptions = async () => {
    try {
      const [countriesRes] = await Promise.all([searchService.getCountries()]);

      if (countriesRes.success) {
        setCountries(countriesRes.data);
      }

      setPositions([
        { id: "", name: "All Positions" },
        { id: "GK", name: "Goalkeeper" },
        { id: "DF", name: "Defender" },
        { id: "MD", name: "Midfielder" },
        { id: "FW", name: "Forward" },
      ]);
    } catch (error) {
      console.error("Error loading filter options:", error);
    }
  };

  // Fetch top news data. Always scoped to the user's selected language so a
  // German viewer sees German football news, not a mixed-locale feed.
  const fetchTopNews = useCallback(async () => {
    setIsLoadingTopNews(true);
    setTopNewsError(null);
    try {
      const response = await dashboardService.getLatestNews(
        10, 0, apiLanguage === "en" ? null : apiLanguage,
      );
      if (response.success && response.data?.results) {
        setTopNews(response.data.results);
      } else {
        setTopNewsError(response.error || "Failed to load top news");
        setTopNews([]);
      }
    } catch (error) {
      setTopNewsError("Failed to load top news");
      setTopNews([]);
    } finally {
      setIsLoadingTopNews(false);
    }
  }, [apiLanguage]);

  // Initial load — runs once; subsequent locale changes re-run fetchTopNews.
  useEffect(() => {
    const loadInitialData = async () => {
      await loadFilterOptions();
      await fetchTopNews();
      setIsInitialLoad(false);
    };

    if (isInitialLoad) {
      loadInitialData();
    }
  }, [isInitialLoad, fetchTopNews]);

  // Re-fetch when language switches (skips the very first run — initial
  // load above already covers that).
  useEffect(() => {
    if (isInitialLoad) return;
    fetchTopNews();
  }, [apiLanguage, fetchTopNews, isInitialLoad]);

  // Handle player click navigation
  const handlePlayerClickNavigate = (playerId) => {
    if (playerId) {
      navigate(`/search/player/${playerId}`);
    }
  };

  // Handle club click navigation
  const handleClubClickNavigate = (clubId) => {
    if (clubId) {
      navigate(`/search/club/${clubId}`);
    }
  };

  // Handle news click navigation.
  // Accepts either an article object (preferred) or a bare article id for
  // backwards compat. We never fall back to the external URL — readers stay
  // on TransferPitch.
  const handleNewsClick = (articleOrId) => {
    const articleId =
      articleOrId && typeof articleOrId === "object"
        ? articleOrId.id
        : articleOrId;
    if (articleId) {
      navigate(`/news/${encodeURIComponent(articleId)}`);
    }
  };

  return {
    // Top news state
    topNews,
    isLoadingTopNews,
    topNewsError,

    // Shared data
    positions,
    countries,

    // Navigation handlers
    handlePlayerClickNavigate,
    handleClubClickNavigate,
    handleNewsClick,

    // Localisation
    apiLanguage,
    changeLanguage,

    // Utility
    fetchTopNews,
  };
};

export default useTransferNews;
